import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import LoadingAnimation from "../animation/LoadingAnimation";
import { createUrl } from "../audio/audioUtil";
import {
  AudioComparison,
  listAudioComparisons,
  listenForAudioComparison,
  RequestedAudioFile,
} from "../firebase/audioComparisons";
import {
  listenForUploadedIncident,
  UploadedIncidentResult,
} from "../firebase/uploadedIncidents";
import { CenteredRow } from "../layout/Row";
import { SmallLogoWithDashboard } from "../logo/SmallLogo";
import { Incident } from "../model/incident";
import OrganisationSummary from "../organisation/OrganisationSummary";
import { ApplicationState } from "../reducers";
import { checkAudioExists } from "../services/audioSliceService";
import { tutToDate } from "../util";
import Player from "./Player";
import "./Player.css";
import { loadScoutNamesIfNeeded } from "./scoutNames";

// The device retries internally for up to 90s before uploading, plus upload time
// on a slow link, so a slice can take a couple of minutes to arrive
const RESPONSE_TIMEOUT_MS = 3 * 60 * 1000;
const POLL_INTERVAL_MS = 10 * 1000;

const Meta = styled.div`
  max-width: 1270px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 2rem;
`;

const RequestSummary = styled.div`
  .Organisation-key-value {
    margin-bottom: 0.125rem;
  }
`;

const FileHeader = styled.div`
  display: flex;
  align-items: baseline;
  h4 {
    margin: 0 0.75rem 0 0;
  }
`;

const StatusMessage = styled.p<{ isError?: boolean }>`
  margin: 0.25rem 0 0 0;
  color: ${(props) => (props.isError ? "#c62828" : "#666")};
`;

// Section headings sitting outside the player cards; side margin lines up with
// the Widget-item cards' own 1rem margin
const SectionTitle = styled.h3`
  margin: 0 1rem 0.25rem 1rem;
`;

const SectionDescription = styled.p`
  margin: 0 1rem 0 1rem;
  color: #666;
`;

// One line summarising the scouts without audio; the per-scout details live in
// the hover tooltip (title attribute), hence the help cursor
const MissingLine = styled.p`
  margin: 1rem 1rem 0 1rem;
  color: #666;
  cursor: help;
`;

const scoutLabel = (
  displayName: string | undefined,
  deviceId: string,
  scoutNames: { [deviceId: string]: string } | undefined,
) => {
  const name = displayName ?? scoutNames?.[deviceId];
  return name ? `${name} | ${deviceId.substring(0, 7)}` : deviceId.substring(0, 7);
};

const downloadName = (deviceId: string, tut: number, extension: string) => {
  // Colons are not allowed in filenames on all platforms, so the ISO time is dashed
  const isoDate = tutToDate(tut).toISOString().replace(/:/g, "-");
  return `${deviceId}_${isoDate}.${extension}`;
};

const slicePlayerIncident = (
  incidentId: string,
  deviceId: string,
  organisationId: string,
  tut: number,
  objectPath: string,
): Incident => ({
  id: incidentId,
  deviceId,
  organisationId,
  tut,
  objectPath,
});

/**
 * The anchor incident's own detection audio, as the reference the requested
 * slices are compared against. Stored as wav (normal upload) or opus (a
 * requested slice, or a device recording opus natively) - probe wav first.
 */
const ReferenceAudio = ({
  comparison,
  organisationId,
  jwtToken,
  scoutNames,
}: {
  comparison: AudioComparison;
  organisationId: string;
  jwtToken: string;
  scoutNames: { [deviceId: string]: string } | undefined;
}) => {
  const [objectPath, setObjectPath] = useState<string | undefined>(undefined);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const wavPath = `inbox/scout/${comparison.deviceId}/${comparison.incidentId}.wav`;
    const opusPath = `inbox/scout/${comparison.deviceId}/${comparison.incidentId}.opus`;
    checkAudioExists(jwtToken, wavPath, (wavExists) => {
      if (wavExists) {
        setObjectPath(wavPath);
      } else {
        checkAudioExists(jwtToken, opusPath, (opusExists) => {
          if (opusExists) {
            setObjectPath(opusPath);
          } else {
            setMissing(true);
          }
        });
      }
    });
  }, [jwtToken, comparison.deviceId, comparison.incidentId]);

  const extension = objectPath?.endsWith(".wav") ? "wav" : "opus";
  return (
    <div className="Widget-item">
      <FileHeader>
        <h4>{scoutLabel(undefined, comparison.deviceId, scoutNames)}</h4>
        <p>detection audio</p>
      </FileHeader>
      {objectPath && (
        <Player
          incident={slicePlayerIncident(
            comparison.incidentId,
            comparison.deviceId,
            organisationId,
            comparison.tut,
            objectPath,
          )}
          jwtToken={jwtToken}
          maxFetchRetries={0}
          url={createUrl(objectPath, "triangula-scout-audio")}
          downloadFilename={downloadName(comparison.deviceId, comparison.tut, extension)}
        />
      )}
      {missing && <StatusMessage>No detection audio in storage for the anchor incident</StatusMessage>}
      {!objectPath && !missing && <StatusMessage>Checking storage…</StatusMessage>}
    </div>
  );
};

interface FileState {
  file: RequestedAudioFile;
  result?: UploadedIncidentResult;
  existsInStorage: boolean;
  timedOut: boolean;
}

const hasAudio = (state: FileState) =>
  state.result?.status === "uploaded" || state.result?.status === "partial" || state.existsInStorage;

/**
 * Tracks every requested file's outcome in one place so the page can render
 * players for the files that got audio and one summary line for the rest.
 * Combines the uploadedIncidents result docs with a storage probe fallback -
 * the result doc can be lost (scout-api restart) or expired (revisiting the
 * page later than 24h) while the audio itself persists.
 */
const useFileStates = (
  comparison: AudioComparison,
  organisationId: string,
  jwtToken: string,
): { [incidentId: string]: FileState } => {
  const [states, setStates] = useState<{ [incidentId: string]: FileState }>({});

  useEffect(() => {
    const initial: { [incidentId: string]: FileState } = {};
    comparison.files.forEach((file) => {
      initial[file.incidentId] = { file, existsInStorage: false, timedOut: false };
    });
    setStates(initial);

    const update = (incidentId: string, patch: Partial<FileState>) =>
      setStates((current) =>
        current[incidentId] ? { ...current, [incidentId]: { ...current[incidentId], ...patch } } : current,
      );

    const unsubscribes = comparison.files.map((file) =>
      listenForUploadedIncident(organisationId, file.incidentId, (uploaded) => {
        // A result doc from an earlier request for the same incident may still
        // exist (they live ~24h); only accept results newer than this request
        if (
          uploaded.createdMs === undefined ||
          comparison.createdMs === undefined ||
          uploaded.createdMs >= comparison.createdMs
        ) {
          update(file.incidentId, { result: uploaded });
        }
      }),
    );

    const startedAt = Date.now();
    const probeAll = () =>
      comparison.files.forEach((file) =>
        checkAudioExists(jwtToken, file.objectPath, (exists) => {
          if (exists) {
            update(file.incidentId, { existsInStorage: true });
          }
        }),
      );
    probeAll();
    const interval = setInterval(() => {
      if (Date.now() - startedAt > RESPONSE_TIMEOUT_MS) {
        setStates((current) => {
          const next: { [incidentId: string]: FileState } = {};
          Object.keys(current).forEach((id) => {
            next[id] = { ...current[id], timedOut: true };
          });
          return next;
        });
        clearInterval(interval);
        return;
      }
      probeAll();
    }, POLL_INTERVAL_MS);

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
      clearInterval(interval);
    };
    // A re-request rewrites the document with a new created timestamp; files keep their ids
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, jwtToken, comparison.incidentId, comparison.createdMs, comparison.files.length]);

  return states;
};

/**
 * One sentence covering every scout without audio, e.g. "No audio yet from 5
 * scouts - 3 had nothing stored for this window, 2 not replied yet". The
 * per-scout details (including the unavailable store-coverage messages) go in
 * the tooltip.
 */
const missingSummary = (
  missing: FileState[],
  scoutNames: { [deviceId: string]: string } | undefined,
) => {
  const label = (state: FileState) => scoutLabel(state.file.displayName, state.file.deviceId, scoutNames);
  const unavailable = missing.filter((state) => state.result?.status === "unavailable");
  const failed = missing.filter((state) => state.result?.status === "failed");
  const silent = missing.filter((state) => state.result === undefined);
  const pending = silent.some((state) => !state.timedOut);

  const clauses: string[] = [];
  const tooltip: string[] = [];
  if (unavailable.length > 0) {
    clauses.push(`${unavailable.length} had nothing stored for this window`);
    tooltip.push("Nothing stored for this window:");
    unavailable.forEach((state) =>
      tooltip.push(
        `${label(state)}${state.result?.message ? ` - ${state.result.message}` : ""}${
          state.result?.storedFromTut && state.result?.storedToTut
            ? ` (its store covers ${tutToDate(state.result.storedFromTut).toISOString()} to ${tutToDate(
                state.result.storedToTut,
              ).toISOString()})`
            : ""
        }`,
      ),
    );
  }
  if (failed.length > 0) {
    clauses.push(`${failed.length} failed to upload`);
    tooltip.push("Failed to upload:");
    failed.forEach((state) =>
      tooltip.push(`${label(state)}${state.result?.message ? ` - ${state.result.message}` : ""}`),
    );
  }
  if (silent.length > 0) {
    clauses.push(pending ? `${silent.length} not replied yet` : `${silent.length} never replied (probably offline)`);
    tooltip.push(pending ? "Not replied yet:" : "Never replied:");
    silent.forEach((state) => tooltip.push(label(state)));
  }

  const scouts = missing.length === 1 ? "scout" : "scouts";
  const lead = pending ? "No audio yet from" : "No audio from";
  return { text: `${lead} ${missing.length} ${scouts} - ${clauses.join(", ")}`, tooltip: tooltip.join("\n") };
};

const UploadedFileItem = ({
  state,
  comparison,
  organisationId,
  jwtToken,
  scoutNames,
}: {
  state: FileState;
  comparison: AudioComparison;
  organisationId: string;
  jwtToken: string;
  scoutNames: { [deviceId: string]: string } | undefined;
}) => {
  const { file, result } = state;
  return (
    <div className="Widget-item">
      <FileHeader>
        <h4>{scoutLabel(file.displayName, file.deviceId, scoutNames)}</h4>
        {result && <p>{result.status}</p>}
      </FileHeader>
      <Player
        incident={slicePlayerIncident(
          file.incidentId,
          file.deviceId,
          organisationId,
          comparison.tut,
          file.objectPath,
        )}
        jwtToken={jwtToken}
        maxFetchRetries={0}
        url={createUrl(file.objectPath, "triangula-scout-audio")}
        downloadFilename={downloadName(file.deviceId, comparison.tut, "opus")}
      />
      {result?.status === "partial" && (
        <StatusMessage>
          Partial slice - the scout only had audio
          {result.storedFromTut ? ` from ${tutToDate(result.storedFromTut).toISOString()}` : ""}
          {result.storedToTut ? ` to ${tutToDate(result.storedToTut).toISOString()}` : ""}
          {result.message ? ` (${result.message})` : ""}
        </StatusMessage>
      )}
    </div>
  );
};

const ComparisonDetail = ({
  comparison,
  organisationId,
  jwtToken,
  scoutNames,
}: {
  comparison: AudioComparison;
  organisationId: string;
  jwtToken: string;
  scoutNames: { [deviceId: string]: string } | undefined;
}) => {
  const states = useFileStates(comparison, organisationId, jwtToken);
  const ordered = comparison.files
    .map((file) => states[file.incidentId])
    .filter((state): state is FileState => state !== undefined);
  const withAudio = ordered.filter(hasAudio);
  const missing = ordered.filter((state) => !hasAudio(state));
  const summary = missing.length > 0 ? missingSummary(missing, scoutNames) : undefined;

  return (
    <>
      <SectionTitle>Reference</SectionTitle>
      <SectionDescription>
        The detection audio the comparison was requested from - compare the uploads below against this recording
      </SectionDescription>
      <ReferenceAudio
        comparison={comparison}
        organisationId={organisationId}
        jwtToken={jwtToken}
        scoutNames={scoutNames}
      />
      <SectionTitle style={{ marginTop: "3rem" }}>Requested uploads</SectionTitle>
      <SectionDescription>
        The same time window as uploaded by every scout that was available around the incident
      </SectionDescription>
      {withAudio.map((state) => (
        <UploadedFileItem
          key={state.file.incidentId}
          state={state}
          comparison={comparison}
          organisationId={organisationId}
          jwtToken={jwtToken}
          scoutNames={scoutNames}
        />
      ))}
      {summary && <MissingLine title={summary.tooltip}>{summary.text}</MissingLine>}
      {comparison.files.length === 0 && (
        <CenteredRow>
          <h2>No scouts were available at the time</h2>
        </CenteredRow>
      )}
    </>
  );
};

const ComparisonList = ({
  comparisons,
  organisationId,
}: {
  comparisons: AudioComparison[];
  organisationId: string;
}) => {
  const history = useHistory();
  if (comparisons.length === 0) {
    return (
      <CenteredRow>
        <h2>No audio comparisons yet for this organisation</h2>
      </CenteredRow>
    );
  }
  return (
    <>
      {comparisons.map((comparison) => (
        <div
          key={comparison.incidentId}
          className="Widget-item"
          // Widget-item's dashboard-grid sizing (flex-basis/grow) would stretch a lone row
          style={{ cursor: "pointer", flexBasis: "auto", flexGrow: 0 }}
          onClick={() =>
            history.push(`/audioComparison?organisation-id=${organisationId}&incident=${comparison.incidentId}`)
          }
        >
          <FileHeader>
            <h4>{tutToDate(comparison.tut).toISOString()}</h4>
            <p>
              {comparison.pre}s pre, {comparison.length}s total · {comparison.files.length} scout
              {comparison.files.length === 1 ? "" : "s"}
              {comparison.triangulationId ? ` · triangulation ${comparison.triangulationId}` : ""}
            </p>
          </FileHeader>
        </div>
      ))}
    </>
  );
};

/**
 * Audio comparison: the slices requested from every scout that was available
 * around an incident ("request audio from every scout in the area" in
 * commander). Deep linked as
 * /#/audioComparison?organisation-id={orgId}&incident={anchorId}; commander
 * opens it right after making the request, so the players fill in live as the
 * scouts upload. Without the incident param the page lists the organisation's
 * comparisons instead.
 */
const AudioComparisonPage = (props: Props) => {
  const [organisationId, setOrganisationId] = useState<string | undefined>(undefined);
  const [incidentId, setIncidentId] = useState<string | undefined>(undefined);
  const [comparison, setComparison] = useState<AudioComparison | undefined>(undefined);
  const [comparisons, setComparisons] = useState<AudioComparison[] | undefined>(undefined);

  const locationSearch: string = (props as any).location.search;

  useEffect(() => {
    const urlParams = new URLSearchParams(locationSearch);
    setOrganisationId(urlParams.get("organisation-id") || undefined);
    setIncidentId(urlParams.get("incident") || undefined);
  }, [locationSearch]);

  useEffect(() => {
    if (!organisationId || !incidentId) {
      return;
    }
    setComparison(undefined);
    loadScoutNamesIfNeeded(organisationId);
    return listenForAudioComparison(organisationId, incidentId, setComparison);
  }, [organisationId, incidentId]);

  useEffect(() => {
    if (!organisationId || incidentId) {
      return;
    }
    setComparisons(undefined);
    listAudioComparisons(organisationId)
      .then(setComparisons)
      .catch((error) => {
        console.error("Failed to list audio comparisons", organisationId, error);
        setComparisons([]);
      });
  }, [organisationId, incidentId]);

  const { jwtToken } = props;
  if (!jwtToken) {
    return <div />;
  }
  if (!organisationId) {
    return (
      <div className={"Dashboard"}>
        <div className={"Dashboard-header"}>
          <SmallLogoWithDashboard fontSizeRem={1.625} />
          <p className={"Dashboard-header-title"}>Audio comparison</p>
        </div>
        <CenteredRow>
          <h2>Missing organisation-id in the URL</h2>
        </CenteredRow>
      </div>
    );
  }

  const scoutNames = props.scoutNames[organisationId];
  return (
    <div className={"Dashboard"}>
      <div className={"Dashboard-header"}>
        <SmallLogoWithDashboard fontSizeRem={1.625} />
        <p className={"Dashboard-header-title"}>Audio comparison</p>
      </div>
      <Meta className={"Organisation-section"}>
        <OrganisationSummary organisationId={organisationId} />
        {incidentId && comparison && (
          <RequestSummary>
            <div className={"Organisation-key-value"}>
              <p>Incident</p>
              <p>{comparison.incidentId}</p>
            </div>
            {comparison.triangulationId && (
              <div className={"Organisation-key-value"}>
                <p>Triangulation</p>
                <p>{comparison.triangulationId}</p>
              </div>
            )}
            <div className={"Organisation-key-value"}>
              <p>Time</p>
              <p>{tutToDate(comparison.tut).toISOString()}</p>
            </div>
            <div className={"Organisation-key-value"}>
              <p>Window</p>
              <p>
                {comparison.pre}s pre, {comparison.length}s total
              </p>
            </div>
            <div className={"Organisation-key-value"}>
              <p>Requested from</p>
              <p>{comparison.files.length} scout(s)</p>
            </div>
          </RequestSummary>
        )}
      </Meta>
      <div className={"Incident-page"}>
        {incidentId && !comparison && (
          <CenteredRow>
            <div>
              <LoadingAnimation height={50} width={50} />
              <p>Waiting for request details…</p>
            </div>
          </CenteredRow>
        )}
        {incidentId && comparison && (
          <ComparisonDetail
            comparison={comparison}
            organisationId={organisationId}
            jwtToken={jwtToken}
            scoutNames={scoutNames}
          />
        )}
        {!incidentId && !comparisons && (
          <CenteredRow>
            <div>
              <LoadingAnimation height={50} width={50} />
              <p>Loading comparisons…</p>
            </div>
          </CenteredRow>
        )}
        {!incidentId && comparisons && (
          <ComparisonList comparisons={comparisons} organisationId={organisationId} />
        )}
      </div>
    </div>
  );
};

interface OwnProps {}

interface StateToProps {
  jwtToken: string | undefined;
  scoutNames: { [organisationId: string]: { [deviceId: string]: string } };
}

interface DispatchFromProps {}

interface Props extends StateToProps, DispatchFromProps, OwnProps {}

const mapStateToProps = (state: ApplicationState) => ({
  jwtToken: state.login.jwtToken,
  scoutNames: state.incident.scoutNames,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({}, dispatch);

export default connect<StateToProps, DispatchFromProps>(
  // @ts-ignore
  mapStateToProps,
  mapDispatchToProps,
)(AudioComparisonPage);
